import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { ArrowLeft, TrendingUp, Lightbulb, MousePointerClick, Eye, ChevronRight } from 'lucide-react';
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from 'recharts';
import { studentAPI } from '../utils/api';
import { getMonsterGif, getMonsterGifByMonster, getMonsterFallbackGif, formatDate, getClassBadge, getClassName, loadUploadedGifs, subscribeToCacheUpdates, getMonsterDisplayName } from '../utils/helpers';
import { toast } from 'sonner';
import PokemonImage from '../components/PokemonImage';
import { SIGNAL_META } from '../components/PerformanceTrafficLight';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const EvolutionHistory = () => {
  const navigate = useNavigate();
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [gifCacheReady, setGifCacheReady] = useState(false);
  const [evolutionPanel, setEvolutionPanel] = useState(null);
  const [signalsSummary, setSignalsSummary] = useState({ green: 0, yellow: 0, red: 0, total_graded: 0, total_essays: 0 });
  const [signalsDetail, setSignalsDetail] = useState({ green: [], yellow: [], red: [] });
  const [openSignal, setOpenSignal] = useState(null); // 'green' | 'yellow' | 'red' | null

  useEffect(() => {
    loadHistory();
    loadEvolutionPanel();
    loadSignalsSummary();
    loadSignalsDetail();
    loadUploadedGifs().then(() => setGifCacheReady(true));
    const unsubscribe = subscribeToCacheUpdates(() => setGifCacheReady(true));
    const pollInterval = setInterval(loadEvolutionPanel, 30000);
    return () => {
      unsubscribe();
      clearInterval(pollInterval);
    };
  }, []);

  const loadSignalsSummary = async () => {
    try {
      const resp = await fetch(`${BACKEND_URL}/api/student/green-signals-summary`, {
        credentials: 'include'
      });
      if (resp.ok) {
        const data = await resp.json();
        setSignalsSummary(data);
      }
    } catch (error) {
      // silent
    }
  };

  const loadSignalsDetail = async () => {
    try {
      const resp = await fetch(`${BACKEND_URL}/api/student/essay-signals-detail`, {
        credentials: 'include'
      });
      if (resp.ok) {
        const data = await resp.json();
        setSignalsDetail({
          green: Array.isArray(data.green) ? data.green : [],
          yellow: Array.isArray(data.yellow) ? data.yellow : [],
          red: Array.isArray(data.red) ? data.red : [],
        });
      }
    } catch (error) {
      // silent
    }
  };

  const openSignalModal = (signal) => {
    const list = signalsDetail[signal] || [];
    if (list.length === 0) {
      toast.info('Você ainda não tem missões nesse desempenho.');
      return;
    }
    setOpenSignal(signal);
  };

  const loadHistory = async () => {
    try {
      const response = await studentAPI.getEvolutionHistory();
      setHistory(response.data);
    } catch (error) {
      console.error('Failed to load evolution history:', error);
      toast.error('Erro ao carregar histórico');
    } finally {
      setLoading(false);
    }
  };

  const loadEvolutionPanel = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/student/evolution-panel`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setEvolutionPanel(data);
      }
    } catch (error) {
      // Silently fail
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F8FAFC]">
        <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      <nav className="bg-white border-b border-slate-200">
        <div className="container mx-auto px-4 py-4">
          <Button
            data-testid="back-to-dashboard-btn"
            onClick={() => navigate('/student/dashboard')}
            variant="ghost"
            className="flex items-center gap-2"
          >
            <ArrowLeft className="w-4 h-4" />
            Voltar
          </Button>
        </div>
      </nav>

      <div className="container mx-auto px-4 py-8">
        <div className="mb-8">
          <h1 className="text-3xl font-outfit font-bold text-slate-900 mb-2" data-testid="history-title">
            Desempenho e Histórico
          </h1>
          <p className="text-slate-600 font-nunito">Acompanhe sua evolução, sequência de estudo e histórico de evoluções</p>
        </div>

        {/* Semáforo de Desempenho Summary */}
        {signalsSummary.total_essays > 0 && (
          <div
            data-testid="traffic-light-summary"
            className="mb-8 relative overflow-hidden rounded-2xl bg-slate-900 text-white p-5 shadow-xl"
          >
            <div className="absolute -right-10 -top-10 w-40 h-40 rounded-full bg-emerald-500/20 blur-3xl" />
            <div className="absolute -left-10 -bottom-10 w-40 h-40 rounded-full bg-rose-500/10 blur-3xl" />

            <div className="relative">
              <div className="flex items-center justify-between mb-4 gap-3 flex-wrap">
                <div>
                  <div className="text-xs font-bold uppercase tracking-[0.18em] text-slate-400">
                    Semáforo de Desempenho
                  </div>
                  <h3 className="text-xl font-outfit font-bold">Sinais acumulados em dissertativas</h3>
                </div>
                <div className="text-xs text-slate-400">
                  {signalsSummary.total_graded} de {signalsSummary.total_essays} já avaliadas pelo Mestre
                </div>
              </div>

              <div className="grid grid-cols-3 gap-3">
                {['green', 'yellow', 'red'].map((k) => {
                  const meta = SIGNAL_META[k];
                  const count = signalsSummary[k] || 0;
                  const hasItems = (signalsDetail[k] || []).length > 0;
                  return (
                    <button
                      type="button"
                      key={k}
                      data-testid={`summary-${k}`}
                      onClick={() => openSignalModal(k)}
                      disabled={!hasItems}
                      className={`group relative rounded-xl p-4 bg-gradient-to-br ${meta.gradient} text-white overflow-hidden text-left transition-transform ${hasItems ? 'hover:scale-[1.03] hover:shadow-2xl cursor-pointer' : 'opacity-90 cursor-default'} focus:outline-none focus:ring-2 focus:ring-white/70`}
                    >
                      <div className="absolute top-2 right-2 w-3 h-3 rounded-full bg-white/90 animate-pulse" />
                      <div className="text-3xl font-extrabold leading-none">
                        {count}
                      </div>
                      <div className="mt-2 text-xs font-bold uppercase tracking-wider opacity-90">
                        {meta.label}
                      </div>
                      {hasItems && (
                        <div className="mt-3 inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-widest bg-white/20 rounded-full px-2 py-1 backdrop-blur-sm group-hover:bg-white/30 transition-colors">
                          <MousePointerClick className="w-3 h-3" /> Clique para revisar
                        </div>
                      )}
                    </button>
                  );
                })}
              </div>

              {signalsSummary.green > 0 && (
                <p className="mt-4 text-sm text-slate-300 italic">
                  &ldquo;Cada sinal verde é um passo de maestria. Continue trilhando, Treinador.&rdquo;
                </p>
              )}
            </div>
          </div>
        )}

        {/* Evolution Panel */}
        {evolutionPanel && (
          <div className="bg-white rounded-2xl border-2 border-slate-100 shadow-sm overflow-hidden mb-8" data-testid="evolution-panel">
            {/* AI Tip */}
            {evolutionPanel.ai_tip && (
              <div className="px-6 pt-5 pb-3" data-testid="master-tip">
                <div className="flex items-start gap-3 bg-gradient-to-r from-indigo-50 to-blue-50 rounded-xl p-4 border border-indigo-100">
                  <Lightbulb className="w-5 h-5 text-indigo-500 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-xs font-bold text-indigo-600 font-outfit uppercase tracking-wide mb-1">Dica de Mestre</p>
                    <p className="text-sm text-slate-700 font-nunito leading-relaxed">{evolutionPanel.ai_tip}</p>
                  </div>
                </div>
              </div>
            )}

            {/* XP Chart */}
            <div className="px-6 py-4" data-testid="xp-chart-section">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-base font-outfit font-bold text-slate-900">AURA por Missão</h3>
                <span className="text-xs text-slate-400 font-nunito">Últimos 7 dias</span>
              </div>
              {evolutionPanel.xp_history.length > 0 ? (
                <div className="h-48 w-full">
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={evolutionPanel.xp_history} margin={{ top: 5, right: 10, left: 0, bottom: 5 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                      <XAxis dataKey="date" tick={{ fontSize: 11, fill: '#94a3b8' }} tickLine={false} axisLine={{ stroke: '#e2e8f0' }} />
                      <YAxis tick={{ fontSize: 11, fill: '#94a3b8' }} tickLine={false} axisLine={false} width={35} />
                      <Tooltip
                        contentStyle={{
                          background: 'rgba(255,255,255,0.95)',
                          border: '1px solid #e2e8f0',
                          borderRadius: '12px',
                          boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                          fontSize: '12px'
                        }}
                        formatter={(value, name) => {
                          if (name === 'xp') return [`${value} AURA`, 'AURA Acumulada'];
                          if (name === 'xp_earned') return [`+${value} AURA`, 'AURA Ganha'];
                          return [value, name];
                        }}
                      />
                      <Line type="monotone" dataKey="xp" stroke="#4f46e5" strokeWidth={2.5} dot={{ fill: '#4f46e5', strokeWidth: 0, r: 4 }} activeDot={{ r: 6, fill: '#4f46e5', stroke: '#fff', strokeWidth: 2 }} />
                      <Line type="monotone" dataKey="xp_earned" stroke="#ec4899" strokeWidth={2} strokeDasharray="5 5" dot={{ fill: '#ec4899', strokeWidth: 0, r: 3 }} activeDot={{ r: 5, fill: '#ec4899', stroke: '#fff', strokeWidth: 2 }} />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              ) : (
                <div className="h-48 flex items-center justify-center bg-slate-50 rounded-xl">
                  <p className="text-sm text-slate-400 font-nunito">Complete missões para ver seu progresso!</p>
                </div>
              )}
            </div>

            {/* Study Streak - Pokeball Widget */}
            <div className="px-6 pb-5" data-testid="study-streak-section">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-base font-outfit font-bold text-slate-900">Sequência de Estudo</h3>
                <span className="text-xs font-semibold text-indigo-600 bg-indigo-50 px-2 py-0.5 rounded-full font-nunito">{evolutionPanel.active_days}/7 dias</span>
              </div>
              <div className="flex items-center justify-between gap-2">
                {['Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb', 'Dom'].map((day, index) => {
                  const isActive = evolutionPanel.streak_days[index];
                  return (
                    <div key={day} className="flex flex-col items-center gap-1.5 flex-1" data-testid={`streak-day-${index}`}>
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center transition-all duration-300 ${
                        isActive
                          ? 'bg-gradient-to-br from-red-500 to-red-600 shadow-lg shadow-red-500/30 scale-110'
                          : 'bg-slate-100 border border-slate-200'
                      }`}>
                        <svg viewBox="0 0 100 100" className={`w-6 h-6 ${isActive ? '' : 'opacity-30'}`}>
                          <circle cx="50" cy="50" r="48" fill={isActive ? '#fff' : '#cbd5e1'} stroke={isActive ? '#1e293b' : '#94a3b8'} strokeWidth="4"/>
                          <path d="M2 50 H98" stroke={isActive ? '#1e293b' : '#94a3b8'} strokeWidth="4"/>
                          <circle cx="50" cy="50" r="16" fill={isActive ? '#fff' : '#e2e8f0'} stroke={isActive ? '#1e293b' : '#94a3b8'} strokeWidth="4"/>
                          <circle cx="50" cy="50" r="8" fill={isActive ? '#1e293b' : '#cbd5e1'}/>
                          <path d="M2 50 Q50 50 50 2 Q50 50 98 50" fill={isActive ? '#ef4444' : '#94a3b8'} opacity={isActive ? 1 : 0.4}/>
                          <circle cx="50" cy="50" r="16" fill={isActive ? '#fff' : '#e2e8f0'} stroke={isActive ? '#1e293b' : '#94a3b8'} strokeWidth="4"/>
                          <circle cx="50" cy="50" r="8" fill={isActive ? '#1e293b' : '#cbd5e1'}/>
                        </svg>
                      </div>
                      <span className={`text-[10px] font-bold font-nunito ${isActive ? 'text-red-600' : 'text-slate-400'}`}>{day}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        )}

        {/* Evolution History */}
        <div className="mb-6">
          <h2 className="text-xl font-outfit font-bold text-slate-900 mb-2">Histórico de Evoluções</h2>
          <p className="text-sm text-slate-500 font-nunito">Todas as evoluções do seu monstro</p>
        </div>

        {history.length === 0 ? (
          <div className="bg-white rounded-2xl border-2 border-slate-100 p-12 text-center">
            <TrendingUp className="w-16 h-16 text-slate-300 mx-auto mb-4" />
            <p className="text-slate-500 font-nunito">Nenhuma evolução registrada ainda</p>
          </div>
        ) : (
          <div className="space-y-4">
            {history.map((evolution) => (
              <div
                key={evolution.evolution_id}
                data-testid="evolution-card"
                className="bg-white rounded-2xl border-2 border-slate-100 p-6 shadow-sm"
              >
                <div className="flex items-center gap-6">
                  <PokemonImage
                    src={getMonsterGifByMonster({...evolution.monster, stage: evolution.to_stage})}
                    fallbackSrc={getMonsterFallbackGif({...evolution.monster, stage: evolution.to_stage})}
                    alt={getMonsterDisplayName(evolution.monster)}
                    className="w-24 h-24 object-contain"
                    fallbackSize={96}
                  />
                  <div className="flex-1">
                    <h3 className="text-xl font-outfit font-bold text-slate-900 mb-2">
                      {getMonsterDisplayName(evolution.monster)}
                    </h3>
                    <div className="flex items-center gap-3 mb-2">
                      <span className={`px-3 py-1 rounded-md text-xs font-bold uppercase border ${getClassBadge(evolution.monster.class_type)}`}>
                        {getClassName(evolution.monster.class_type)}
                      </span>
                    </div>
                    <div className="flex items-center gap-4 text-sm text-slate-600 font-nunito">
                      <div className="flex items-center gap-2">
                        <TrendingUp className="w-4 h-4 text-secondary" />
                        <span>
                          Estágio {evolution.from_stage} → Estágio {evolution.to_stage}
                        </span>
                      </div>
                      <span>•</span>
                      <span>Nível {evolution.level_at_evolution}</span>
                      <span>•</span>
                      <span>{formatDate(evolution.evolved_at)}</span>
                    </div>
                  </div>
                  <div className="bg-gradient-to-br from-purple-500 to-indigo-600 text-white px-6 py-3 rounded-xl">
                    <p className="text-xs font-nunito opacity-90">Estágio</p>
                    <p className="text-3xl font-outfit font-black">{evolution.to_stage}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Signal Detail Modal — clickable cards on traffic-light summary */}
      <Dialog open={!!openSignal} onOpenChange={(v) => { if (!v) setOpenSignal(null); }}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto" data-testid="signal-detail-dialog">
          {openSignal && (() => {
            const meta = SIGNAL_META[openSignal];
            const list = signalsDetail[openSignal] || [];
            return (
              <>
                <DialogHeader>
                  <DialogTitle className="font-outfit flex items-center gap-3">
                    <span className={`inline-block w-4 h-4 rounded-full bg-gradient-to-br ${meta.gradient}`} />
                    <span className={`${meta.text}`}>{meta.label}</span>
                    <span className="text-sm font-normal text-slate-500">({list.length} {list.length === 1 ? 'missão' : 'missões'})</span>
                  </DialogTitle>
                  <p className="text-xs text-slate-500 italic mt-1">{meta.description}</p>
                </DialogHeader>

                <div className="mt-4 space-y-2" data-testid="signal-detail-list">
                  {list.map((item, idx) => (
                    <button
                      key={`${item.submission_id}-${item.question_index}-${idx}`}
                      type="button"
                      onClick={() => {
                        setOpenSignal(null);
                        navigate(`/student/missions?review=${item.mission_id}`);
                      }}
                      data-testid={`signal-item-${idx}`}
                      className={`w-full text-left rounded-xl border-2 p-3 transition-all hover:shadow-md hover:-translate-y-0.5 ${meta.border} ${meta.soft}`}
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0 flex-1">
                          <p className={`font-outfit font-bold text-sm truncate ${meta.text}`}>
                            {item.mission_title}
                          </p>
                          {item.question_text && (
                            <p className="text-xs text-slate-600 mt-1 line-clamp-2">
                              {item.question_text}
                            </p>
                          )}
                          <div className="flex items-center gap-2 mt-2 flex-wrap">
                            <span className="text-[10px] font-bold uppercase tracking-wider text-slate-500">
                              {formatDate(item.submitted_at)}
                            </span>
                            {item.graded ? (
                              <span className="text-[10px] font-bold uppercase tracking-wider bg-emerald-100 text-emerald-700 px-2 py-0.5 rounded-full">
                                Nota do Mestre: {item.teacher_score}
                              </span>
                            ) : (
                              <span className="text-[10px] font-bold uppercase tracking-wider bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full">
                                Aguardando Mestre
                              </span>
                            )}
                            {item.gabarito_released && (
                              <span className="text-[10px] font-bold uppercase tracking-wider bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full">
                                Gabarito liberado
                              </span>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center gap-1 text-slate-400 self-center">
                          <Eye className="w-4 h-4" />
                          <ChevronRight className="w-4 h-4" />
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
              </>
            );
          })()}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default EvolutionHistory;
